import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Archive } from '@/assets/icons';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import type { DiagramKind, DiagramMeta } from '@/diagram-persistence/diagram-store';
import { useDiagramList } from '@/pages/diagram-gallery/use-diagram-list';

import { CheckoutOffer } from './upgrade-dialog';

/** One row of the limit dialog: a diagram that an Archive can act on. */
export type ActiveDiagram = { id: string; name: string; kind: DiagramKind };

/**
 * Narrows gallery metas to the diagrams the limit dialog can archive, most
 * recently edited first. Archived ones are already out of the way. A `localOnly`
 * one exists on this device only until its upload lands, so the server has
 * nothing to archive yet; offering the button would just fail.
 */
export function toActiveDiagrams(metas: readonly DiagramMeta[]): ActiveDiagram[] {
    return metas
        .filter((m) => !m.archived && !m.localOnly)
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((m) => ({ id: m.id, name: m.name, kind: m.kind }));
}

/**
 * Every active diagram of both kinds, or null until both lists have loaded.
 * It lists the store, so call it only while the dialog is open.
 */
export function useActiveDiagrams(): ActiveDiagram[] | null {
    const crochet = useDiagramList('crochet').metas;
    const knitting = useDiagramList('knitting').metas;
    return useMemo(
        () => (crochet == null || knitting == null ? null : toActiveDiagrams([...crochet, ...knitting])),
        [crochet, knitting],
    );
}

const KIND_LABEL = {
    crochet: 'billing.limit.kind.crochet',
    knitting: 'billing.limit.kind.knitting',
} as const satisfies Record<DiagramKind, string>;

/**
 * The list inside the open dialog, so the store is listed only while someone is
 * looking. One archive at a time: every button waits while one is in flight.
 */
function DiagramList({
    useDiagrams,
    onArchive,
}: {
    useDiagrams: () => ActiveDiagram[] | null;
    onArchive: (id: string) => Promise<boolean>;
}) {
    const { t } = useTranslation();
    const diagrams = useDiagrams();
    const [busyId, setBusyId] = useState<string | null>(null);

    const archive = async (id: string) => {
        setBusyId(id);
        // On success the dialog is closing, so the buttons stay disabled through the exit.
        if (!(await onArchive(id))) setBusyId(null);
    };

    if (diagrams == null) return <p className="text-sm text-muted-foreground">{t('billing.limit.loading')}</p>;
    // Nothing the server could archive, e.g. every diagram over the limit is still device-only. An empty
    // list would read as a glitch; say why, and leave Pro and Dismiss as the ways on.
    if (diagrams.length === 0) return <p className="text-sm text-muted-foreground">{t('billing.limit.empty')}</p>;

    return (
        <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto" aria-busy={busyId != null}>
            {diagrams.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                    <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{d.name}</p>
                        <p className="text-xs text-muted-foreground">{t(KIND_LABEL[d.kind])}</p>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={t('billing.limit.archiveNamed', { name: d.name })}
                        disabled={busyId != null}
                        onClick={() => void archive(d.id)}
                    >
                        <Archive aria-hidden="true" />
                        {t('billing.limit.archive')}
                    </Button>
                </li>
            ))}
        </ul>
    );
}

/**
 * Shown when a free account is out of active diagrams. It lists them with an
 * Archive on each, since archiving is the way to get a slot back, and offers Pro
 * when it can be bought. Navigation-dumb like the `/try` sign-up wall: the
 * provider decides when it opens, what an archive does and where checkout goes.
 */
export function LimitDialog({
    open,
    limit,
    checkoutHref,
    useDiagrams,
    onArchive,
    onDismiss,
}: {
    open: boolean;
    /** The plan's active-diagram limit; null while it isn't known. */
    limit: number | null;
    checkoutHref: string | null;
    /** Where the rows come from. Called as a hook, once, inside the open dialog. */
    useDiagrams: () => ActiveDiagram[] | null;
    /** Resolves true once the diagram is archived, false if it failed (the provider has said so). */
    onArchive: (id: string) => Promise<boolean>;
    onDismiss: () => void;
}) {
    const { t } = useTranslation();
    const contentRef = useRef<HTMLDivElement>(null);
    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onDismiss();
            }}
        >
            <DialogContent
                ref={contentRef}
                // The first tabbable is the most recently edited diagram's Archive: focused on open, a
                // keyboard Enter would archive it. Start on the dialog itself; Tab reaches the rows.
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                    contentRef.current?.focus();
                }}
            >
                <DialogHeader>
                    <DialogTitle>{t('billing.limit.title')}</DialogTitle>
                    <DialogDescription>
                        {limit != null ? t('billing.limit.body', { limit }) : t('billing.limit.bodyNoLimit')}
                    </DialogDescription>
                </DialogHeader>
                <DiagramList useDiagrams={useDiagrams} onArchive={onArchive} />
                <CheckoutOffer
                    checkoutHref={checkoutHref}
                    labels={{
                        dismiss: t('billing.limit.dismiss'),
                        checkout: t('billing.limit.checkout'),
                        unavailable: t('billing.limit.proUnavailable'),
                    }}
                    onDismiss={onDismiss}
                />
            </DialogContent>
        </Dialog>
    );
}
